import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PrivacyProvider } from './contexts/PrivacyContext';
import Login from './pages/LoginPage';
import Register from './pages/RegisterPage';
import ResetPassword from './pages/ResetPasswordPage';
import Activities from './pages/Activities';
import DailyView from './pages/Activities/DailyView';
import WeeklyView from './pages/Activities/WeeklyView';
import MonthlyView from './pages/Activities/MonthlyView';

import AdminActivities from './pages/admin/Activities';
import AdminArtifacts from './pages/admin/Artifacts';
import AdminTeamDynamics from './pages/admin/TeamDynamics';
import AdminWorkAssessmentResults from './pages/admin/WorkAssessmentResults';
import AdminWorkAssessment from './pages/admin/WorkAssessment';
import ReleaseNotesMaintenancePage from './pages/Maintenance/ReleaseNotes';
import MaintenanceBannerPage from './pages/Maintenance/Banner';
import IssueReportsPage from './pages/Maintenance/IssueReports';

import Members from './pages/Members';
import Artifacts from './pages/Artifacts';
import Settings from './pages/Settings';
import AdminHome from './pages/Home/Home-Admin';
import StudentHome from './pages/Home/Home-Student';
import './App.css';
import GroupViewer from "./pages/Home/GroupViewer";
import GroupManager from "./pages/Home/GroupManager";
import CourseSettings from "./pages/admin/CourseSettings";
import RepoPage from "./pages/Connections/RepoPage"
import GroupSetupPage from "./pages/Connections/GroupSetUp";
import DiscordSetUpPage from "./pages/Connections/DiscordSetUp";
import ConnectionPage from './pages/Connections';
import FigmaSetupPage from './pages/Connections/FigmaSetUp';
import PrivacyPolicy from './pages/LegalDoc/PrivacyPolicy';
import TermsOfService from './pages/LegalDoc/TermsOfService';
import WorkAssessment from './pages/WorkAssessment';
import AuthSetupPage from './pages/Connections/AuthSetUp';
import DiscordClaimRedirect from './components/DiscordClaim/Redirect';
import MaintenanceBanner from './components/common/MaintenanceBanner';
import FeedbackButton from './components/common/FeedbackButton';
import ReleaseNotesDialog from './components/common/ReleaseNotesDialog';
import { Toaster } from 'sonner';
import { initErrorLogger } from './errorLogger';

initErrorLogger();

const ProtectedRoute = () => {
    const { user } = useAuth();
    return user ? <Outlet /> : <Navigate to="/login" replace />;
};

const AdminProtectedRoute = () => {
    const { user, isAdmin, loading } = useAuth();
    
    if (loading) {
        return <div>Loading...</div>;
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    if (!isAdmin) {
        return <Navigate to="/student/home" replace />;
    }
    
    return <Outlet />;
};

const ActivitiesViewProtectedRoute = () => {
    const { courseInfo } = useAuth();
    return courseInfo ? <Outlet /> : <Navigate to="/student/home" replace />;
};

const AdminActivitiesViewProtectedRoute = () => {
    const { courseInfo } = useAuth();
    return courseInfo ? <Outlet /> : <Navigate to="/admin/home" replace />;
};

// Redirect authenticated users away from auth pages
const AuthRedirectRoute = ({ children }) => {
    const { user, isAdmin, loading } = useAuth();

    if (loading) {
        return <div>Loading...</div>; // or your loading component
    }

    if (user) {
        // Redirect authenticated users to appropriate home page
        return isAdmin ? <Navigate to="/admin/home" replace /> : <Navigate to="/student/home" replace />;
    }

    return children;
};

const App = () => (
    <div className="App">
        <BrowserRouter>
            <PrivacyProvider>
            <AuthProvider>
                <Toaster position="top-center" richColors closeButton />
                <MaintenanceBanner />
                <FeedbackButton />
                <ReleaseNotesDialog />
                <Routes>
                    {/* Public authentication routes */}
                    <Route path="/login" element={
                        <AuthRedirectRoute>
                            <Login />
                        </AuthRedirectRoute>
                    } />
                    <Route path="/register" element={
                        <AuthRedirectRoute>
                            <Register />
                        </AuthRedirectRoute>
                    } />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Protected student routes */}
                    <Route element={<ProtectedRoute />}>
                        {/* <Route element={<StudentProtectedRoute />}> */}
                        <Route path="/student/home" element={<StudentHome />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/maintenance/release-notes" element={<ReleaseNotesMaintenancePage />} />
                        <Route path="/maintenance/banner" element={<MaintenanceBannerPage />} />
                        <Route path="/maintenance/issues" element={<IssueReportsPage />} />
                        {/* </Route> */}
                        <Route element={<ActivitiesViewProtectedRoute />}>
                            <Route path="/activities" element={<Activities />}>
                                <Route index element={<DailyView />} />
                                <Route path="daily" element={<DailyView />} />
                                <Route path="weekly" element={<WeeklyView />} />
                                <Route path="monthly" element={<MonthlyView />} />
                            </Route>
                            <Route path="/artifacts" element={<Artifacts />} />
                            <Route path="/work-assessment" element={<WorkAssessment />} />
                            <Route path="/members" element={<Members />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/repo-setup" element={<RepoPage />} />
                            <Route path="/group-setup" element={<GroupSetupPage />} />
                            <Route path="/discord-setup" element={<DiscordSetUpPage />} />
                            <Route path="/connections" element={<ConnectionPage />} />
                            <Route path="/figma-setup" element={<FigmaSetupPage />} />
                            <Route path="/auth-setup" element={<AuthSetupPage />} />
                        </Route>
                        <Route path="/discord/claim/:draftId" element={<DiscordClaimRedirect />} />
                    </Route>

                    {/* Protected admin routes */}
                    <Route element={<AdminProtectedRoute />}>
                        <Route path="/admin/home" element={<AdminHome />} />
                        <Route path="/admin/home/group_viewer" element={<GroupViewer />} />
                        <Route path="/admin/home/group_manager" element={<GroupManager />} />
                        <Route path="/admin/course-settings/:courseId" element={<CourseSettings />} />
                        <Route path="/admin/release-notes" element={<Navigate to="/maintenance/release-notes" replace />} />
                        <Route element={<AdminActivitiesViewProtectedRoute />}>
                            <Route path="/admin_activities" element={<AdminActivities />}>
                                <Route index element={<DailyView />} />
                                <Route path="daily" element={<DailyView />} />
                                <Route path="weekly" element={<WeeklyView />} />
                                <Route path="monthly" element={<MonthlyView />} />
                            </Route>
                            <Route path="/admin_artifacts" element={<AdminArtifacts />} />
                            <Route path="/admin_artifacts_results" element={<AdminWorkAssessmentResults />} />
                            <Route path="/admin_work_assessment_results" element={<AdminWorkAssessmentResults />} />
                            <Route path="/admin_team_dynamics" element={<AdminTeamDynamics />} />
                            <Route path="/admin_work_assessment" element={<AdminWorkAssessment />} />
                        </Route>
                    </Route>

                    {/* Public legal documents */}
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />

                    {/* Default redirect */}
                    <Route path="/" element={<Navigate to="/login" replace />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
            </AuthProvider>
            </PrivacyProvider>
        </BrowserRouter>
    </div>
);

export default App;
